import React, { useEffect, useRef, useState } from 'react';

interface EditableButtonProps {
  label: string;
  value?: string;
  placeholder?: string;
  onSubmit: (next: string) => void;
  transform?: (raw: string) => string;
  additionalStyles?: React.CSSProperties;
}

const EditableButton = ({
  label,
  value,
  placeholder = 'Set value',
  onSubmit,
  transform,
  additionalStyles
}: EditableButtonProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(value ?? '');
    }
  }, [isEditing, value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const startEditing = () => {
    setIsEditing(true);
  };

  const commitEditing = () => {
    const raw = draft;
    const trimmed = transform ? transform(raw) : raw.trim();
    if (trimmed.length === 0) {
      setIsEditing(false);
      return;
    }
    onSubmit(trimmed);
    setIsEditing(false);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' || e.key === 'Select' || e.key === 'GoBack' || e.key === 'Cancel') {
      e.preventDefault();
      commitEditing();
    }
  };

  const handleOnFocus = () => {
    const container = window.document.getElementById('editable-button-input')?.parentElement;
    container?.classList.add('editable-button-input-focused');
  }
  
  const handleOnBlur = () => {
    const container = window.document.getElementById('editable-button-input')?.parentElement;
    container?.classList.remove('editable-button-input-focused');
  };

  return (
    <>
      {!isEditing && (
        <button type="button" className="tv-button" onClick={startEditing} tabIndex={0} style={additionalStyles}>
          <span style={{ display: 'block', fontSize: '0.75rem', color: '#9eb2c7' }}>{label}</span>
          <span style={{ display: 'block', marginTop: '0.35rem' }}>
            {value && value.length > 0 ? value : placeholder}
          </span>
        </button>
      )}

      {isEditing && (
        <div className="tv-button" style={{ padding: '0.75rem 1rem', ...additionalStyles }}>
          <span style={{ display: 'block', fontSize: '0.75rem', color: '#9eb2c7' }}>{label}</span>
          <div className="editable-button-controls">
            <input
              id='editable-button-input'
              ref={inputRef}
              className='editable-button-input'
              onFocus={() => handleOnFocus()}
              onBlur={() => handleOnBlur()}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label={label}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default EditableButton;
